import type {
  CoachDevelopmentMemory,
  CoachLeaderSessionInterpretation,
  CoachRuntimeContextType,
  LeaderDevelopmentMemory,
  MeetingEvent,
  MeetingInterpretation,
} from "@/lib/behavior/behaviorContracts";
import {
  buildCoachDigitalTwinRuntimeFromSources,
} from "@/lib/behavior/coachRuntime";
import { buildMeetingEvidenceSources } from "@/lib/behavior/meetingSystemOfRecord";
import type {
  CoachLensSource,
  CoachReviewV1,
  CoachTwinSource,
  LeaderPlanDraftV1,
} from "@/lib/coachSessionReviewContract";

type TranscriptLike = {
  id?: string | null;
  leader_id?: string | null;
  meeting_id?: string | null;
  summary_text?: string | null;
  analysis_status?: string | null;
  created_at?: string | null;
};

type SessionRecordLike = {
  id?: string | null;
};

type PracticeSummaryLike = {
  id?: string | null;
  meeting_id?: string | null;
  transcript_summary?: string | null;
};

type CoachMemoryLike = {
  recentNodes?: Array<{ id?: string | null }>;
};

export function buildCoachDevelopmentMemory(params: {
  coachReview: CoachReviewV1;
}): CoachDevelopmentMemory {
  return {
    coachingStrengths: params.coachReview.coachingStrengths,
    coachingGrowthEdges: params.coachReview.coachingGrowthEdges,
    nextCoachingExperiment: params.coachReview.nextCoachingExperiment,
    reflectiveQuestions: params.coachReview.reflectiveQuestions,
    frameworkAlignmentNotes: params.coachReview.frameworkAlignmentNotes,
    deliveryStyleNotes: params.coachReview.deliveryStyleNotes,
  };
}

export function buildLeaderDevelopmentMemory(params: {
  leaderPlanDraft: LeaderPlanDraftV1;
}): LeaderDevelopmentMemory {
  return {
    discussed: params.leaderPlanDraft.whatDiscussed,
    nextFocus: params.leaderPlanDraft.nextFocus,
    nextSession: params.leaderPlanDraft.nextSession,
    actionItems: params.leaderPlanDraft.actionItems,
    reflectionPrompts: params.leaderPlanDraft.reflectionPrompts,
  };
}

export function buildCoachLeaderSessionInterpretation(params: {
  meeting: MeetingEvent;
  meetingInterpretation: MeetingInterpretation;
  transcript?: TranscriptLike | null;
  coachReview: CoachReviewV1;
  leaderPlanDraft: LeaderPlanDraftV1;
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
  coachMemory?: CoachMemoryLike | null;
  sessionRecord?: SessionRecordLike | null;
  practiceSummary?: PracticeSummaryLike | null;
  source: "saved" | "derived";
  publishedToLeader: boolean;
  contextType?: CoachRuntimeContextType;
}): CoachLeaderSessionInterpretation {
  const runtime = buildCoachDigitalTwinRuntimeFromSources({
    coachLens: params.coachLens,
    coachTwin: params.coachTwin,
    contextType: params.contextType ?? "session",
  });

  const evidenceSources = buildMeetingEvidenceSources({
    transcript: params.transcript,
    practiceSummary: params.practiceSummary || {
      id: null,
      meeting_id: params.meetingInterpretation.meetingId,
      transcript_summary: params.meetingInterpretation.summarySource === "practice_summary"
        ? params.meetingInterpretation.summaryText
        : null,
    },
    sessionRecord: params.sessionRecord,
    coachLens: params.coachLens,
    coachTwin: params.coachTwin,
    coachMemory: params.coachMemory,
    meetingInterpretation: params.meetingInterpretation,
  });

  return {
    version: "coach_leader_session_interpretation_v1",
    meeting: params.meeting,
    meetingInterpretation: {
      ...params.meetingInterpretation,
      evidenceSources,
    },
    evidenceSources,
    coachDevelopment: buildCoachDevelopmentMemory({ coachReview: params.coachReview }),
    leaderDevelopment: buildLeaderDevelopmentMemory({ leaderPlanDraft: params.leaderPlanDraft }),
    runtime,
    source: params.source,
    analysisContext: "coach_leader_session",
    analysisStatus: params.transcript?.analysis_status ?? null,
    publishedToLeader: params.publishedToLeader,
  };
}
